import * as React from 'react';
import cx from 'classnames';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { mdx, MDXProvider } from '@mdx-js/react';
import ImageZoom from 'react-medium-image-zoom';
import { HeadingLink } from '@invertase/ui';

import Link from './Link';
import BlockQuote from './BlockQuote';
import CodeBlock from './CodeBlock';
import Vimeo from './Vimeo';

interface Props {
  children: string;
}

const text = 'font-thin leading-loose';

const components = {
  // Headings
  h1: (props: any) => (
    <HeadingLink size="h1" as="h2" id={props.id}>
      {props.children}
    </HeadingLink>
  ),
  h2: (props: any) => (
    <HeadingLink size="h2" as="h3" id={props.id}>
      {props.children}
    </HeadingLink>
  ),
  h3: (props: any) => (
    <HeadingLink size="h3" as="h4" id={props.id}>
      {props.children}
    </HeadingLink>
  ),
  h4: (props: any) => (
    <HeadingLink size="h4" as="h5" id={props.id}>
      {props.children}
    </HeadingLink>
  ),
  h5: (props: any) => (
    <HeadingLink size="h5" as="h6" id={props.id}>
      {props.children}
    </HeadingLink>
  ),

  // Text
  p: ({ children }: any) => {
    return <p className={cx('my-4', text)}>{children}</p>;
  },

  // Code
  pre: ({ className, children }: any) => <CodeBlock className={className}>{children}</CodeBlock>,
  code: ({ className, ...props }: any) => {
    return <code {...props} className={className || 'language-text'} />;
  },

  // Lists
  ul: (props: any) => <ul {...props} className="list-disc list-inside pl-4" />,
  ol: (props: any) => <ul {...props} className="list-decimal list-inside pl-4" />,
  li: (props: any) => <li {...props} className={cx('mb-2', text)} />,

  // Table
  table: (props: any) => (
    <table
      {...props}
      className="overflow-x-auto border-collapse"
      style={{ width: 'list-content' }}
    />
  ),

  // Meta
  img: ({ src, alt, ...other }: any) => (
    <figure className="block my-6 text-center">
      <ImageZoom
        image={{
          ...other,
          src,
          alt,
          className: 'mx-auto rounded',
          style: { maxHeight: 400 },
        }}
        zoomImage={{
          src,
          alt,
          className: 'rounded',
        }}
        defaultStyles={{
          overlay: {
            backgroundColor: '#1A212C',
          },
        }}
      />
      {!!alt && <figcaption className="mt-4 text-xs">{alt}</figcaption>}
    </figure>
  ),
  a: ({ href, children }: any) => (
    <Link to={href} target="auto">
      {children}
    </Link>
  ),
  blockquote: ({ children }: any) => <BlockQuote>{children}</BlockQuote>,

  Vimeo,
};

// Renders a fully parsed JSX markdown string
function Markdown({ children }: Props) {
  return (
    <MDXProvider components={components}>
      <MDXRenderer>{children}</MDXRenderer>
    </MDXProvider>
  );
}

// Renders a React component as markdown
Markdown.Provider = function MarkdownProvider({ children }: any) {
  return <MDXProvider components={components}>{children}</MDXProvider>;
};

// Converts a raw MDX code string into a component
Markdown.Raw = function MarkdownRaw({ children }: Props) {
  if (!children) return null;

  let fn;

  try {
    fn = new Function('React', 'mdx', `${children}; return React.createElement(MDXContent)`);
  } catch (e) {
    throw e;
  }

  // @ts-ignore
  const element = fn(React, mdx);
  return <Markdown.Provider>{element}</Markdown.Provider>;
};

export default Markdown;
